// CorrectedAgeManager/correctedAgeUtils.ts
import {TimeDifference, GraphType} from './types';
import { differenceInDays, differenceInMonths, differenceInYears } from 'date-fns';

// Calculate days passed between a given date and today
export const getDaysPassed = (dateString: string): number => {
    const [day, month, year] = dateString.split('/');
    const date = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    
    // Reset time part for both dates to compare just the dates
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Check if date is in the past
  export const isDatePassed = (dateString: string): boolean => {
    return getDaysPassed(dateString) > 0;
  };
  
  // Get text showing how many days a date is past due
  export const getDatePastDueText = (dateString: string): string => {
    const daysPassed = getDaysPassed(dateString);
    if (daysPassed <= 0) return '';
    return `(התאריך עבר לפני ${daysPassed} ימים)`;
  };
  
  // Get short text showing that the date is past due
  export const getDatePastDueTextShortMsg = (dateString: string): string => {
    const daysPassed = getDaysPassed(dateString);
    if (daysPassed <= 0) return '';
    return `(התאריך עבר)`;
};

  // Get style object for date text based on whether it's passed
  export const getDateStyle = (dateString: string) => ({
    color: isDatePassed(dateString) ? 'gray' : 'inherit'
  });
  
  // Style for past due text
  export const pastDueStyle = {
    color: 'gray',
    marginRight: '8px'
  } as const;

  // functin to calculate the difference between two given dates and return a string with the result
  export const getDateDifference = (startDate: Date, endDate: Date, startMonthToPresentInMonths: number = 0): string => {
    // Ensure end date is later than start date
    if (startDate > endDate) {
      [startDate, endDate] = [endDate, startDate];
    }
  
    const totalDays = differenceInDays(endDate, startDate);
    // If less than a week, just return days
    if (totalDays < 7) {
      return totalDays === 1 ? 'יום אחד' : `${totalDays} ימים`;
    }
  
    const years = differenceInYears(endDate, startDate);
    
    // Calculate remaining months after years
    const afterYears = new Date(startDate);
    afterYears.setFullYear(afterYears.getFullYear() + years);
    const months = differenceInMonths(endDate, afterYears);
    
    // calculating only weeks and days if current months didn't reach startMonthToPresentInMonths
    if (startMonthToPresentInMonths > 0 && months < startMonthToPresentInMonths && years===0) {
      const totalWeeks = Math.floor(totalDays / 7);
      const remainingDays = totalDays % 7;
      
      const parts: string[] = [];
      
      if (totalWeeks > 0) {
        parts.push(`${totalWeeks === 1 ? 'שבוע אחד' : `${totalWeeks} שבועות`}`);
      }
      
      if (remainingDays > 0) {
        const dayText = remainingDays === 1 ? 'יום אחד' : `${remainingDays} ימים`;
        let prefix = '';
        if (totalWeeks > 0) {
            prefix = remainingDays === 1 ? ' ו' : ' ו-';
        }
        parts.push(`${prefix}${dayText}`);
      }
      
      return parts.join('');
    }
  
    // Continue with regular calculation if months >= startMonthToPresentInMonths or if years>1
    const afterMonths = new Date(afterYears);
    afterMonths.setMonth(afterMonths.getMonth() + months);
    const days = differenceInDays(endDate, afterMonths);
    
    // Calculate weeks and remaining days
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
  
    const difference: TimeDifference = {
      years,
      months,
      weeks,
      days: remainingDays
    };

    // Format the output string
    const parts: string[] = [];
    
    if (difference.years > 0) {
      if (difference.years === 1) {parts.push('שנה אחת');}
      else if (difference.years === 2) {parts.push('שנתיים');}
      else {parts.push(`${difference.years} שנים`);}
    }
    
    if (difference.months > 0) {
      const monthText = difference.months === 1 ? 'חודש אחד' : `${difference.months} חודשים`;
      let prefix = '';
      if (difference.years > 0 && difference.weeks === 0 && difference.days === 0) {
          prefix = difference.months === 1 ? 'ו' : 'ו-';
      }
      parts.push(`${prefix}${monthText}`);
    }
    
    if (difference.weeks > 0) {  
      const weekText = difference.weeks === 1 ? 'שבוע אחד' : `${difference.weeks} שבועות`;
      let prefix = '';
      if ((difference.years > 0 || difference.months >0) && (difference.days === 0)) {
          prefix = difference.weeks === 1 ? 'ו' : 'ו-';
      }
      parts.push(`${prefix}${weekText}`);
    }

    if (difference.days > 0) {
      const dayText = difference.days === 1 ? 'יום אחד' : `${difference.days} ימים`;
      let prefix = '';
      if (difference.years > 0 || difference.months > 0 || difference.weeks > 0) {
          prefix = difference.days === 1 ? 'ו' : 'ו-';
      }
      parts.push(`${prefix}${dayText}`);
    }
  
    return parts.reduce((result, part, index) => {
      if (index === 0) return part;
      if (index === parts.length - 1) {
          // Check if the current part starts with 'ו' or 'ו-'
          if (part.startsWith('ו')) return `${result} ${part}`;
          return `${result}, ${part}`;
      }
      return `${result}, ${part}`;
    }, '') || '';
  };

  // Determine graph type based on conditions
const safeParseInt = (value: string | ''): number | undefined => {
  if (value === '') return undefined;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? undefined : n;
};

export const getGraphType = (
  fixedAgeInWeeks: number | '',
  gestationWeeks: string | ''
  ): GraphType => {
    const fixedAge = fixedAgeInWeeks === '' ? undefined : fixedAgeInWeeks; // number | undefined
    const gestWeeks = safeParseInt(gestationWeeks); // number | undefined
    if ((typeof fixedAge === 'number' && fixedAge > 50) || (typeof gestWeeks === 'number' && gestWeeks >= 37)) {
        return 'WHO';
    }
    return 'פנטון';
  };

/** Types */
export interface ThreePoint {
  week: number;
  value: number;  // measurement value (V)
  z?: number;     // Z-score (if known)
  p?: number;     // percentile (if known; can be 0..1 or 0..100)
}